import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { 
  FiPackage, FiMapPin, FiUser, FiPhone, FiFileText,
  FiArrowLeft, FiAlertTriangle, FiRefreshCw, FiCheck,
  FiDollarSign
} from 'react-icons/fi';
import toast from 'react-hot-toast';

const ExpressRequestPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const serviceType = searchParams.get('tipo') || 'ponto_a_ponto';
  
  const [loading, setLoading] = useState(false);
  const [returnTrip, setReturnTrip] = useState(false);
  
  const [formData, setFormData] = useState({
    pickup_address: '',
    pickup_cep: '',
    pickup_complement: '',
    delivery_address: '',
    delivery_cep: '',
    delivery_complement: '',
    item_description: '',
    notes: '',
    recipient_name: '',
    recipient_phone: ''
  });

  // Scroll para o topo quando a página carrega
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!user) {
      toast.error('Faça login para solicitar este serviço');
      navigate('/login');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const calculateTotal = () => {
    if (serviceType === 'leva_traz') {
      return returnTrip ? 40.00 : 20.00;
    }
    return 20.00;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.item_description.length < 10) {
      toast.error('Descreva o item com mais detalhes (mínimo 10 caracteres)');
      return;
    }

    // Verificar palavras proibidas
    const forbiddenWords = ['comida', 'alimento', 'refeição', 'marmita', 'lanche', 'pizza', 'hamburguer', 'sushi', 'açaí', 'sorvete', 'bolo', 'doce', 'salgado'];
    const descLower = formData.item_description.toLowerCase();
    for (const word of forbiddenWords) {
      if (descLower.includes(word)) {
        toast.error('Não transportamos alimentos ou comida. Por favor, descreva outro tipo de item.');
        return;
      }
    }

    setLoading(true);
    try {
      const response = await api.post('/express', {
        service_type: serviceType,
        ...formData,
        return_trip: returnTrip
      });
      
      toast.success('Pedido criado! Redirecionando para pagamento...');
      navigate(`/express/pagamento/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar pedido');
    } finally {
      setLoading(false);
    }
  };

  const serviceName = serviceType === 'leva_traz' ? 'Leva e Traz' : 'Ponto a Ponto';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-bold text-gray-900">Solicitar {serviceName}</h1>
            <p className="text-sm text-gray-500">Preencha os dados abaixo</p>
          </div>
        </div>
      </header>

      {/* Aviso */}
      <div className="bg-red-50 border-b border-red-200 py-3 px-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <FiAlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-700 text-sm">
            <strong>Atenção:</strong> NÃO transportamos alimentos, comida ou produtos perecíveis.
          </p>
        </div>
      </div>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Tipo de serviço */}
          {serviceType === 'leva_traz' && (
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <FiRefreshCw className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">Opção de Retorno</span>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={returnTrip}
                  onChange={(e) => setReturnTrip(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">
                  Quero ida e volta (o item será devolvido ao endereço de origem)
                </span>
              </label>
            </div>
          )}

          {/* Endereço de Coleta */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <FiMapPin className="w-4 h-4 text-green-600" />
              </div>
              <h2 className="font-semibold text-gray-900">Endereço de Coleta</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereço completo *</label>
                <input
                  type="text"
                  name="pickup_address"
                  value={formData.pickup_address}
                  onChange={handleChange}
                  required
                  placeholder="Rua, número, bairro"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                <input
                  type="text"
                  name="pickup_cep"
                  value={formData.pickup_cep}
                  onChange={handleChange}
                  placeholder="00000-000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                <input
                  type="text"
                  name="pickup_complement"
                  value={formData.pickup_complement}
                  onChange={handleChange}
                  placeholder="Apto, bloco, referência"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>
            </div>
          </div>

          {/* Endereço de Entrega */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <FiMapPin className="w-4 h-4 text-red-600" />
              </div>
              <h2 className="font-semibold text-gray-900">Endereço de Entrega</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereço completo *</label>
                <input
                  type="text"
                  name="delivery_address"
                  value={formData.delivery_address}
                  onChange={handleChange}
                  required
                  placeholder="Rua, número, bairro"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                <input
                  type="text"
                  name="delivery_cep"
                  value={formData.delivery_cep}
                  onChange={handleChange}
                  placeholder="00000-000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                <input
                  type="text"
                  name="delivery_complement"
                  value={formData.delivery_complement}
                  onChange={handleChange}
                  placeholder="Apto, bloco, referência"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>
            </div>
          </div>

          {/* Destinatário */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <FiUser className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="font-semibold text-gray-900">Quem vai receber?</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do destinatário *</label>
                <input
                  type="text"
                  name="recipient_name"
                  value={formData.recipient_name}
                  onChange={handleChange}
                  required
                  placeholder="Nome completo"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone do destinatário *</label>
                <input
                  type="tel"
                  name="recipient_phone"
                  value={formData.recipient_phone}
                  onChange={handleChange}
                  required
                  placeholder="(11) 99999-9999"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>
            </div>
          </div>

          {/* Descrição do Item */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <FiPackage className="w-4 h-4 text-purple-600" />
              </div>
              <h2 className="font-semibold text-gray-900">O que será transportado?</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição detalhada do item *
                </label>
                <textarea
                  name="item_description"
                  value={formData.item_description}
                  onChange={handleChange}
                  required
                  rows={3}
                  minLength={10}
                  placeholder="Ex: Envelope com documentos, caixa pequena com peças de roupa, chave de casa em chaveiro vermelho..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Mínimo 10 caracteres. Descreva cor, tamanho e características do item.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações adicionais
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Instruções especiais, horário preferido, etc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 resize-none"
                />
              </div>
            </div>
          </div>

          {/* Resumo */}
          <div className="bg-gray-900 text-white rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <FiDollarSign className="w-5 h-5" />
              <h2 className="font-semibold">Resumo do Pedido</h2>
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-gray-300">
                <span>Serviço:</span>
                <span>{serviceName}</span>
              </div>
              {serviceType === 'leva_traz' && (
                <div className="flex justify-between text-gray-300">
                  <span>Tipo:</span>
                  <span>{returnTrip ? 'Ida e Volta' : 'Somente Ida'}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-300">
                <span>Taxa de coleta:</span>
                <span>R$ 20,00</span>
              </div>
              {returnTrip && (
                <div className="flex justify-between text-gray-300">
                  <span>Taxa de retorno:</span>
                  <span>R$ 20,00</span>
                </div>
              )}
            </div>
            
            <div className="border-t border-gray-700 pt-4 flex justify-between items-center">
              <span className="font-semibold text-lg">Total:</span>
              <span className="text-2xl font-bold text-yellow-400">
                R$ {calculateTotal().toFixed(2)}
              </span>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-red-600 text-white rounded-xl font-semibold text-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <span>Criando pedido...</span>
            ) : (
              <>
                <FiCheck className="w-5 h-5" />
                Continuar para Pagamento
              </>
            )}
          </button>
        </form>
      </main>
    </div>
  );
};

export default ExpressRequestPage;
